import { useCallback, useEffect, useRef, useState } from "react";
import type { ActExplanation, ActExplanationBlock } from "../types/adventure";

interface LessonScreenProps {
  explanation: ActExplanation;
  onContinue: () => void;
}

const SCROLL_BOTTOM_THRESHOLD = 24;

export function LessonScreen({ explanation, onContinue }: LessonScreenProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showCta, setShowCta] = useState(false);

  const updateCtaVisibility = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowCta((prev) => prev || distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    setShowCta(false);
    updateCtaVisibility();

    el.addEventListener("scroll", updateCtaVisibility, { passive: true });
    const resizeObserver = new ResizeObserver(updateCtaVisibility);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener("scroll", updateCtaVisibility);
      resizeObserver.disconnect();
    };
  }, [explanation, updateCtaVisibility]);

  return (
    <div className="act-overlay act-overlay--lesson" role="dialog" aria-modal="true">
      <div className="act-lesson jari-card" aria-labelledby="act-lesson-title">
        <div className="act-lesson__scroll" ref={scrollRef}>
          <h2 id="act-lesson-title" className="act-lesson__title">
            {explanation.title}
          </h2>

          {explanation.sections.map((section, sectionIndex) => (
            <section key={sectionIndex} className="act-lesson__section">
              {section.heading && (
                <h3 className="act-lesson__heading">{section.heading}</h3>
              )}
              {section.blocks.map((block, blockIndex) => (
                <LessonBlock key={blockIndex} block={block} />
              ))}
            </section>
          ))}
        </div>

        {showCta && (
          <footer className="act-lesson__footer">
            <button type="button" className="jari-btn jari-btn--primary" onClick={onContinue}>
              {explanation.cta}
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}

function LessonBlock({ block }: { block: ActExplanationBlock }) {
  switch (block.type) {
    case "paragraph":
      return (
        <p
          className="act-lesson__p"
          dangerouslySetInnerHTML={{ __html: formatMarkdownBold(block.text) }}
        />
      );
    case "helpfulExamples":
      return (
        <ul className="act-lesson__examples act-lesson__examples--helpful" aria-label="Handige gedachten">
          {block.items.map((item, i) => (
            <li key={i}>&ldquo;{item}&rdquo;</li>
          ))}
        </ul>
      );
    case "unhelpfulExamples":
      return (
        <ul
          className="act-lesson__examples act-lesson__examples--unhelpful"
          aria-label="Minder helpende gedachten"
        >
          {block.items.map((item, i) => (
            <li key={i}>&ldquo;{item}&rdquo;</li>
          ))}
        </ul>
      );
    case "questions":
      return (
        <ul className="act-lesson__questions">
          {block.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
  }
}

function formatMarkdownBold(text: string): string {
  return text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
}
